import { useTranslation } from 'react-i18next';
import { Film, Volume2, VolumeX } from 'lucide-react';
import { useEditorStore } from '../store/editorStore';
import { getToneAudioEngine } from '../renderer/rendererBridge';
import { RULER_HEIGHT, TRACK_HEIGHT, TRACK_GAP, EFFECT_TRACK_HEIGHT } from '../lib/canvasDrawing';
import { VolumeSlider } from './VolumeSlider';
import { PanKnob } from './PanKnob';
import { VUMeter } from './VUMeter';

export function TrackLabels() {
  const { t } = useTranslation();
  const tracks = useEditorStore((s) => s.project.tracks);
  const setTrackVolume = useEditorStore((s) => s.setTrackVolume);
  const setTrackPan = useEditorStore((s) => s.setTrackPan);
  const toggleTrackMute = useEditorStore((s) => s.toggleTrackMute);
  const toggleTrackSolo = useEditorStore((s) => s.toggleTrackSolo);

  const syncToEngine = (trackId: string, volume: number, pan: number) => {
    const engine = getToneAudioEngine();
    if (!engine) return;
    engine.setTrackVolume(trackId, volume);
    engine.setTrackPan(trackId, pan);
  };

  return (
    <div
      className="shrink-0 bg-[#111113] border-e border-[#2A2A2E]"
      style={{ width: 140 }}
    >
      {/* Empty spacer matching the ruler height */}
      <div style={{ height: RULER_HEIGHT }} />

      {/* One label per track */}
      {tracks.map((track, i) => {
        const isEffect = track.type === 'effect';
        const trackHeight = isEffect ? EFFECT_TRACK_HEIGHT : TRACK_HEIGHT;

        return (
          <div
            key={track.id}
            className="flex flex-col gap-0.5 px-1.5 py-1"
            style={{
              height: trackHeight,
              marginTop: i > 0 ? TRACK_GAP : 0,
            }}
          >
            {/* Row 1: Icon + Name + S/M buttons */}
            <div className="flex items-center gap-1">
              {isEffect
                ? <span className="text-[8px] font-bold text-[#A855F7]">FX</span>
                : track.type === 'video'
                  ? <Film size={12} color="#8E8E93" />
                  : <Volume2 size={12} color="#8E8E93" />
              }
              <span className={`text-[9px] font-mono truncate flex-1 ${isEffect ? 'text-[#A855F7]' : 'text-[#8E8E93]'}`}>
                {track.name}
              </span>
              {track.type === 'audio' && (
                <>
                  <button
                    onClick={() => {
                      toggleTrackSolo(track.id);
                      const engine = getToneAudioEngine();
                      if (engine) engine.setTrackSolo(track.id, !track.soloed);
                    }}
                    className={`text-[8px] font-bold px-1 rounded ${track.soloed ? 'bg-yellow-500 text-black' : 'text-[#636366] hover:text-[#8E8E93]'}`}
                    title={t('editor.solo', 'Solo')}
                  >
                    S
                  </button>
                  <button
                    onClick={() => {
                      toggleTrackMute(track.id);
                      const engine = getToneAudioEngine();
                      if (engine) engine.setTrackMute(track.id, !track.muted);
                    }}
                    className={`${track.muted ? 'text-red-400' : 'text-[#636366] hover:text-[#8E8E93]'}`}
                    title={track.muted ? t('editor.unmute', 'Unmute') : t('editor.mute', 'Mute')}
                  >
                    {track.muted ? <VolumeX size={12} /> : <Volume2 size={12} />}
                  </button>
                </>
              )}
            </div>

            {/* Row 2: Volume + Pan (audio tracks only) */}
            {track.type === 'audio' && (
              <div className="flex items-center gap-1">
                <VolumeSlider
                  value={track.volume ?? 1}
                  onChange={(v) => {
                    setTrackVolume(track.id, v);
                    syncToEngine(track.id, v, track.pan ?? 0);
                  }}
                />
                <PanKnob
                  value={track.pan ?? 0}
                  onChange={(p) => {
                    setTrackPan(track.id, p);
                    syncToEngine(track.id, track.volume ?? 1, p);
                  }}
                />
                <VUMeter trackId={track.id} orientation="vertical" size="compact" />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
